import { useState } from 'react';
import { DEFECT_SEVERITY_ORDER, type DefectSeverity } from '../../domain/severity';
import type { ReportOutputLanguage } from '../../domain/report';
import { translate, useT, type MessageKey } from '../../i18n/i18n';
import { severityLabel } from '../../i18n/labels';
import { currentReportBranding, removeReportLogoAction, setReportLogoAction, useReportBranding, useReportLogoUrl, writeReportBranding, type ReportBranding } from '../../state/reportBranding';
import { Button, Card, Field, InlineMessage, SectionHeader, SwitchRow, TextInput, showToast } from '../../ui/components';

// Settings › Report branding (Owner decision list 2026-10-19, items 16 and 18): what the inspector's
// reports carry besides the inspection itself — the company block on the cover and in every page's
// running header (name, telephone, e-mail, website, logo), the inspector's own photo beside the inspector
// details, and the inspector's own names for the three defect tiers (the colours are fixed). Everything
// is optional; an empty field prints nothing.
const LANGS: ReadonlyArray<{ lang: ReportOutputLanguage; label: MessageKey }> = [
  { lang: 'en-US', label: 'branding.tierNamesEnglish' },
  { lang: 'zh-CN', label: 'branding.tierNamesChinese' }
];

export function ReportBrandingPage() {
  const { t } = useT();
  const stored = useReportBranding();
  const logoUrl = useReportLogoUrl();
  const [draft, setDraft] = useState<ReportBranding>(stored);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  function change(patch: Partial<ReportBranding>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setSaved(false);
  }

  function changeTier(lang: ReportOutputLanguage, tier: DefectSeverity, value: string) {
    setDraft((prev) => ({ ...prev, severityLabels: { ...prev.severityLabels, [lang]: { ...prev.severityLabels[lang], [tier]: value } } }));
    setSaved(false);
  }

  function save() {
    // The logo is written the moment it is picked; Save writes the fields over what is stored NOW.
    const next = writeReportBranding({ ...draft, logoUpdatedAt: currentReportBranding().logoUpdatedAt });
    setDraft(next);
    setSaved(true);
  }

  async function pickLogo() {
    setBusy(true);
    try {
      const outcome = await setReportLogoAction();
      if (outcome.kind === 'error') showToast(t('branding.logoFailed', { message: outcome.message }), 'info');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card testId="branding-company">
        <SectionHeader title={t('branding.companyTitle')} />
        <p className="card-note">{t('branding.companyBody')}</p>
        <div className="branding-logo" data-testid="branding-logo" data-has-logo={logoUrl ? 'true' : 'false'}>
          {logoUrl ? <img className="branding-logo-img" src={logoUrl} alt="" /> : <span className="branding-logo-empty">{t('branding.noLogo')}</span>}
          <div className="branding-logo-actions">
            <Button variant="secondary" size="small" block={false} icon="photo_library" onClick={() => void pickLogo()} disabled={busy} testId="branding-logo-pick">
              {logoUrl ? t('branding.logoChange') : t('branding.logoPick')}
            </Button>
            {logoUrl ? (
              <Button variant="text" size="small" block={false} onClick={() => void removeReportLogoAction()} disabled={busy} testId="branding-logo-remove">
                {t('branding.logoRemove')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="form-stack">
          <Field label={t('branding.companyName')} htmlFor="branding-company-name">
            <TextInput id="branding-company-name" value={draft.companyName} onChange={(companyName) => change({ companyName })} placeholder={t('branding.companyNamePlaceholder')} testId="branding-company-name" />
          </Field>
          <Field label={t('branding.phone')} htmlFor="branding-phone">
            <TextInput id="branding-phone" value={draft.phone} onChange={(phone) => change({ phone })} inputMode="tel" testId="branding-phone" />
          </Field>
          <Field label={t('branding.email')} htmlFor="branding-email">
            <TextInput id="branding-email" value={draft.email} onChange={(email) => change({ email })} inputMode="email" testId="branding-email" />
          </Field>
          <Field label={t('branding.website')} htmlFor="branding-website">
            <TextInput id="branding-website" value={draft.website} onChange={(website) => change({ website })} placeholder={t('branding.websitePlaceholder')} testId="branding-website" />
          </Field>
        </div>
        <SwitchRow label={t('branding.showInspectorPhoto')} on={draft.showInspectorPhoto} onToggle={(showInspectorPhoto) => change({ showInspectorPhoto })} testId="branding-show-photo" />
        <p className="card-note">{t('branding.showInspectorPhotoHint')}</p>
      </Card>

      <Card testId="branding-tiers">
        <SectionHeader title={t('branding.tiersTitle')} />
        <p className="card-note">{t('branding.tiersBody')}</p>
        {LANGS.map(({ lang, label }) => (
          <div className="form-stack branding-tier-lang" key={lang} data-testid={`branding-tiers-${lang}`}>
            <p className="branding-tier-lang-label">{t(label)}</p>
            {DEFECT_SEVERITY_ORDER.map((tier) => {
              const standard = severityLabel((key, params) => translate(lang, key, params), tier);
              return (
                <Field key={tier} label={standard} trailing={<span className={`branding-tier-swatch branding-tier-${tier}`} aria-hidden="true" />} htmlFor={`branding-tier-${lang}-${tier}`}>
                  <TextInput id={`branding-tier-${lang}-${tier}`} value={draft.severityLabels[lang][tier] ?? ''} onChange={(value) => changeTier(lang, tier, value)} placeholder={standard} testId={`branding-tier-${lang}-${tier}`} />
                </Field>
              );
            })}
          </div>
        ))}
      </Card>

      {saved ? <InlineMessage kind="ok" text={t('branding.saved')} /> : null}
      <Button onClick={save} testId="branding-save">
        {t('common.save')}
      </Button>
    </>
  );
}
